import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, LogOut, Lock, Users, Heart, X, Trash2 } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ADMIN } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PWD_KEY = "wedding_admin_pwd";

export default function Admin() {
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const saved = sessionStorage.getItem(PWD_KEY);
    if (!saved) {
      setChecking(false);
      return;
    }
    (async () => {
      try {
        await axios.post(`${API}/admin/login`, { password: saved });
        setPassword(saved);
        setAuthed(true);
      } catch {
        sessionStorage.removeItem(PWD_KEY);
      } finally {
        setChecking(false);
      }
    })();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!password) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem(PWD_KEY, password);
      setAuthed(true);
      toast.success("Welcome.");
    } catch {
      toast.error("Incorrect password.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(PWD_KEY);
    setPassword("");
    setAuthed(false);
  };

  if (checking) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-golddeep" />
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-6 bg-cream/30">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md bg-white border border-edge p-10 rounded-sm shadow-sm"
        >
          <div className="flex items-center gap-2 eyebrow text-golddeep">
            <Lock className="w-3.5 h-3.5" strokeWidth={1.6} /> Admin
          </div>
          <h1 className="font-display text-4xl mt-3 text-navy">Private view</h1>
          <p className="mt-3 text-navy/60 text-sm">
            Enter the admin password to see RSVP responses.
          </p>
          <form onSubmit={handleLogin} className="mt-8 space-y-6">
            <div>
              <label htmlFor="admin-pwd" className="eyebrow text-navy/60">
                Password
              </label>
              <input
                id="admin-pwd"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="editorial-input mt-2"
                data-testid={ADMIN.passwordInput}
                autoFocus
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid={ADMIN.loginButton}
              className="w-full bg-navy text-cream py-3.5 text-sm uppercase tracking-[0.25em] hover:bg-gold hover:text-navy transition disabled:opacity-60 flex items-center justify-center gap-3"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enter"}
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  return <Dashboard password={password} onLogout={handleLogout} />;
}

function Dashboard({ password, onLogout }) {
  const [stats, setStats] = useState(null);
  const [rsvps, setRsvps] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = { "X-Admin-Password": password };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/rsvps`, { headers }),
      ]);
      setStats(s.data);
      setRsvps(r.data);
    } catch {
      toast.error("Failed to load RSVPs.");
    } finally {
      setLoading(false);
    }
  }, [password]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this RSVP?")) return;
    try {
      await axios.delete(`${API}/admin/rsvps/${id}`, { headers });
      toast.success("Removed.");
      fetchAll();
    } catch {
      toast.error("Couldn’t delete that one.");
    }
  };

  return (
    <div className="bg-cream/30 min-h-[90vh] py-12 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <p className="eyebrow text-golddeep">Admin</p>
            <h1 className="font-display text-4xl sm:text-5xl mt-2 text-navy">
              RSVP responses
            </h1>
            <p className="text-navy/60 mt-2 text-sm">
              Suvansh &amp; Hitanksha · Wed, 25 Nov 2026 · Delhi
            </p>
          </div>
          <button
            onClick={onLogout}
            data-testid={ADMIN.logoutButton}
            className="inline-flex items-center gap-2 eyebrow text-navy/60 hover:text-golddeep transition border border-edge px-4 py-2 hover:border-gold"
          >
            <LogOut className="w-3.5 h-3.5" strokeWidth={1.6} /> Sign out
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
          <StatCard
            label="Total responses"
            value={stats?.total ?? "—"}
            testId={ADMIN.statTotal}
            icon={<Users className="w-4 h-4" strokeWidth={1.6} />}
          />
          <StatCard
            label="Joyfully yes"
            value={stats?.joyfully_yes ?? "—"}
            testId={ADMIN.statYes}
            icon={<Heart className="w-4 h-4 text-golddeep" strokeWidth={1.6} />}
            highlight
          />
          <StatCard
            label="Regretfully no"
            value={stats?.regretfully_no ?? "—"}
            testId={ADMIN.statNo}
            icon={<X className="w-4 h-4" strokeWidth={1.6} />}
          />
          <StatCard
            label="+1s coming"
            value={stats?.plus_ones ?? "—"}
            testId={ADMIN.statPlusOnes}
          />
          <StatCard
            label="Total guests"
            value={stats?.total_guests ?? "—"}
            testId={ADMIN.statGuests}
            highlight
          />
        </div>

        <div className="bg-white border border-edge rounded-sm shadow-sm overflow-hidden">
          {loading ? (
            <div className="py-20 flex items-center justify-center text-navy/50">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : rsvps.length === 0 ? (
            <div
              data-testid={ADMIN.emptyState}
              className="py-20 text-center text-navy/50"
            >
              <p className="font-display text-2xl text-navy/70">
                No responses yet
              </p>
              <p className="text-sm mt-2">
                Share your link — the first RSVP will appear here.
              </p>
            </div>
          ) : (
            <Table data-testid={ADMIN.rsvpsTable}>
              <TableHeader>
                <TableRow className="border-edge hover:bg-transparent">
                  <TableHead className="eyebrow text-navy/60 py-4">Name</TableHead>
                  <TableHead className="eyebrow text-navy/60">Phone</TableHead>
                  <TableHead className="eyebrow text-navy/60">Attending</TableHead>
                  <TableHead className="eyebrow text-navy/60">+1</TableHead>
                  <TableHead className="eyebrow text-navy/60">Received</TableHead>
                  <TableHead className="eyebrow text-navy/60 text-right">&nbsp;</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rsvps.map((r) => (
                  <TableRow
                    key={r.id}
                    className="border-edge hover:bg-cream/40 transition"
                  >
                    <TableCell className="py-5 font-medium text-navy">
                      {r.name}
                    </TableCell>
                    <TableCell className="text-navy/70">{r.phone}</TableCell>
                    <TableCell>
                      <AttendingBadge value={r.attending} />
                    </TableCell>
                    <TableCell className="text-navy/70">
                      {r.attending === "yes"
                        ? r.plus_one === "yes"
                          ? "Yes"
                          : "No"
                        : "—"}
                    </TableCell>
                    <TableCell className="text-navy/60 text-sm">
                      {formatDate(r.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <button
                        onClick={() => handleDelete(r.id)}
                        data-testid={`${ADMIN.deleteRow}-${r.id}`}
                        className="text-navy/40 hover:text-golddeep transition p-2"
                        aria-label="Delete RSVP"
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={1.6} />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, testId, icon, highlight }) {
  return (
    <div
      data-testid={testId}
      className={[
        "bg-white border rounded-sm p-5",
        highlight ? "border-gold/50" : "border-edge",
      ].join(" ")}
    >
      <div className="flex items-center gap-2 eyebrow text-navy/60">
        {icon}
        <span>{label}</span>
      </div>
      <div
        className={[
          "font-display mt-3 text-4xl leading-none",
          highlight ? "text-golddeep" : "text-navy",
        ].join(" ")}
      >
        {value}
      </div>
    </div>
  );
}

function AttendingBadge({ value }) {
  const yes = value === "yes";
  return (
    <span
      className={[
        "inline-flex items-center gap-1.5 px-2.5 py-1 text-xs tracking-wide border rounded-sm",
        yes
          ? "border-gold/50 text-golddeep bg-gold/[0.08]"
          : "border-edge text-navy/60 bg-cream/40",
      ].join(" ")}
    >
      <span
        className={[
          "w-1.5 h-1.5 rounded-full",
          yes ? "bg-gold" : "bg-navy/30",
        ].join(" ")}
      />
      {yes ? "Joyfully yes" : "Regretfully no"}
    </span>
  );
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
}
